import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useBlockchain } from '../../contexts/BlockchainContext';
import { useAlert } from '../../contexts/AlertContext';

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 30px;
  background: rgba(255, 255, 255, 0.1);
  padding: 20px;
  border-radius: 15px;
  backdrop-filter: blur(10px);
`;

const Title = styled.h1`
  color: white;
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
`;

const NetworkInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const NetworkBadge = styled.div`
  background: ${props => props.connected ? '#4ade80' : '#ef4444'};
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.connected ? '#4ade80' : '#ef4444'};
  animation: ${props => props.connected ? 'pulse 2s infinite' : 'none'};
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatsCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 25px;
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatsTitle = styled.h3`
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 10px 0;
  font-size: 1rem;
  font-weight: 500;
`;

const StatsValue = styled.div`
  color: white;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 5px;
`;

const StatsSubtext = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 25px;
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 20px;
`;

const CardTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
`;

const RefreshButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
`;

const BlocksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-height: 400px;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const BlockItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 15px;
  border-radius: 10px;
  border-left: 3px solid #4ade80;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(5px);
  }
`;

const BlockNumber = styled.div`
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 5px;
`;

const BlockInfo = styled.div`
  display: flex;
  justify-content: between;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const TransactionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 400px;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const TransactionItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 12px;
  border-radius: 8px;
  border-left: 3px solid #3b82f6;
`;

const TxHash = styled.div`
  color: white;
  font-family: monospace;
  font-size: 0.9rem;
  margin-bottom: 5px;
  word-break: break-all;
`;

const TxInfo = styled.div`
  display: flex;
  justify-content: between;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
`;

const SearchBox = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  backdrop-filter: blur(10px);
  margin-bottom: 20px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    outline: none;
    border-color: #4ade80;
    box-shadow: 0 0 0 2px rgba(74, 222, 128, 0.2);
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  padding: 40px;
  font-style: italic;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 20px;
`;

const MetricItem = styled.div`
  text-align: center;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

const MetricLabel = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin-bottom: 5px;
`;

const MetricValue = styled.div`
  color: white;
  font-size: 1.3rem;
  font-weight: 600;
`;

const BlockchainExplorer = () => {
  const { 
    isConnected, 
    network, 
    blockHeight, 
    gasPrice, 
    syncStatus, 
    peers, 
    nodeInfo,
    reconnect 
  } = useBlockchain();
  
  const { addAlert } = useAlert();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [networkStats, setNetworkStats] = useState({
    totalTransactions: 0,
    avgBlockTime: 12,
    totalBlocks: 0,
    networkHashrate: '1.2 TH/s',
    difficulty: '23.5T',
    mempool: 1234
  });

  // Generate mock blocks and transactions
  useEffect(() => {
    const generateMockBlocks = () => {
      const newBlocks = [];
      for (let i = 0; i < 10; i++) {
        newBlocks.push({
          number: blockHeight - i,
          hash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
          timestamp: new Date(Date.now() - i * 12000).toISOString(),
          transactions: Math.floor(Math.random() * 100) + 1,
          gasUsed: Math.floor(Math.random() * 15000000) + 5000000,
          gasLimit: 15000000,
          miner: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
          size: Math.floor(Math.random() * 50000) + 10000
        });
      }
      setBlocks(newBlocks);
    };

    const generateMockTransactions = () => {
      const newTxs = [];
      for (let i = 0; i < 20; i++) {
        newTxs.push({
          hash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
          from: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
          to: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
          value: (Math.random() * 10).toFixed(4),
          gasPrice: (Math.random() * 50 + 10).toFixed(2),
          timestamp: new Date(Date.now() - i * 5000).toISOString(),
          status: Math.random() > 0.1 ? 'success' : 'failed'
        });
      }
      setRecentTransactions(newTxs);
    };

    generateMockBlocks();
    generateMockTransactions();

    // Update stats
    setNetworkStats(prev => ({
      ...prev,
      totalBlocks: blockHeight,
      totalTransactions: blockHeight * 75,
      mempool: Math.floor(Math.random() * 1000) + 500
    }));
  }, [blockHeight]);

  // Real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() < 0.3) { // 30% chance of new transaction
        const newTx = {
          hash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
          from: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
          to: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
          value: (Math.random() * 10).toFixed(4),
          gasPrice: (Math.random() * 50 + 10).toFixed(2),
          timestamp: new Date().toISOString(),
          status: Math.random() > 0.1 ? 'success' : 'failed'
        };
        
        setRecentTransactions(prev => [newTx, ...prev.slice(0, 19)]);
        
        if (newTx.status === 'failed') {
          addAlert({
            type: 'blockchain',
            severity: 'medium',
            title: 'Transaction Failed',
            message: `Transaction ${newTx.hash.substr(0, 10)}... failed`,
            category: 'blockchain'
          });
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [addAlert]);

  const handleRefresh = async () => {
    try {
      await reconnect();
      addAlert({
        type: 'system',
        severity: 'low',
        title: 'Blockchain Data Refreshed',
        message: 'Successfully updated blockchain information',
        category: 'system'
      });
    } catch (error) {
      addAlert({
        type: 'blockchain',
        severity: 'high',
        title: 'Refresh Failed',
        message: 'Failed to refresh blockchain data',
        category: 'blockchain'
      });
    }
  };

  const formatHash = (hash, length = 10) => {
    return `${hash.substr(0, length)}...${hash.substr(-length)}`;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const filteredBlocks = blocks.filter(block => 
    block.number.toString().includes(searchQuery) ||
    block.hash.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTransactions = recentTransactions.filter(tx =>
    tx.hash.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tx.from.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tx.to.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Container>
      <Header>
        <Title>🔗 Blockchain Explorer</Title>
        <NetworkInfo>
          <NetworkBadge connected={isConnected}>
            <StatusIndicator connected={isConnected} />
            {network || 'Mainnet'} {isConnected ? 'Connected' : 'Disconnected'}
          </NetworkBadge>
          <RefreshButton onClick={handleRefresh}>
            🔄 Refresh
          </RefreshButton>
        </NetworkInfo>
      </Header>

      <DashboardGrid>
        <StatsCard>
          <StatsTitle>Current Block</StatsTitle>
          <StatsValue>{formatNumber(blockHeight)}</StatsValue>
          <StatsSubtext>Latest block height</StatsSubtext>
        </StatsCard>
        
        <StatsCard>
          <StatsTitle>Gas Price</StatsTitle>
          <StatsValue>{gasPrice} Gwei</StatsValue>
          <StatsSubtext>Current average</StatsSubtext>
        </StatsCard>
        
        <StatsCard>
          <StatsTitle>Active Peers</StatsTitle>
          <StatsValue>{peers}</StatsValue>
          <StatsSubtext>Connected nodes</StatsSubtext>
        </StatsCard>
      </DashboardGrid>

      <SearchBox
        type="text"
        placeholder="Search by block number, hash, or transaction hash..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <ContentGrid>
        <MainContent>
          <Card>
            <CardHeader>
              <CardTitle>📦 Recent Blocks</CardTitle>
              <RefreshButton onClick={handleRefresh}>
                Refresh
              </RefreshButton>
            </CardHeader>
            
            {filteredBlocks.length > 0 ? (
              <BlocksList>
                {filteredBlocks.map((block) => (
                  <BlockItem 
                    key={block.number}
                    onClick={() => setSelectedBlock(block)}
                  >
                    <BlockNumber>Block #{formatNumber(block.number)}</BlockNumber>
                    <BlockInfo>
                      <span>{formatHash(block.hash)}</span>
                      <span>{block.transactions} txs</span>
                      <span>{formatTime(block.timestamp)}</span>
                    </BlockInfo>
                  </BlockItem>
                ))}
              </BlocksList>
            ) : (
              <NoDataMessage>No blocks found matching your search</NoDataMessage>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>💸 Recent Transactions</CardTitle>
            </CardHeader>
            
            {filteredTransactions.length > 0 ? (
              <TransactionsList>
                {filteredTransactions.map((tx) => (
                  <TransactionItem key={tx.hash}>
                    <TxHash>{formatHash(tx.hash, 12)}</TxHash>
                    <TxInfo>
                      <span>{formatHash(tx.from)} → {formatHash(tx.to)}</span>
                      <span style={{ color: tx.status === 'success' ? '#4ade80' : '#ef4444' }}>
                        {tx.value} XCB
                      </span>
                    </TxInfo>
                  </TransactionItem>
                ))}
              </TransactionsList>
            ) : (
              <NoDataMessage>No transactions found matching your search</NoDataMessage>
            )}
          </Card>
        </MainContent>

        <Sidebar>
          <Card>
            <CardTitle>📊 Network Statistics</CardTitle>
            <MetricsGrid>
              <MetricItem>
                <MetricLabel>Total Blocks</MetricLabel>
                <MetricValue>{formatNumber(networkStats.totalBlocks)}</MetricValue>
              </MetricItem>
              <MetricItem>
                <MetricLabel>Total Transactions</MetricLabel>
                <MetricValue>{formatNumber(networkStats.totalTransactions)}</MetricValue>
              </MetricItem>
              <MetricItem>
                <MetricLabel>Avg Block Time</MetricLabel>
                <MetricValue>{networkStats.avgBlockTime}s</MetricValue>
              </MetricItem>
              <MetricItem>
                <MetricLabel>Mempool</MetricLabel>
                <MetricValue>{formatNumber(networkStats.mempool)}</MetricValue>
              </MetricItem>
              <MetricItem>
                <MetricLabel>Hashrate</MetricLabel>
                <MetricValue>{networkStats.networkHashrate}</MetricValue>
              </MetricItem>
              <MetricItem>
                <MetricLabel>Difficulty</MetricLabel>
                <MetricValue>{networkStats.difficulty}</MetricValue>
              </MetricItem>
            </MetricsGrid>
          </Card>

          <Card>
            <CardTitle>⚙️ Node Information</CardTitle>
            <MetricsGrid>
              <MetricItem>
                <MetricLabel>Node Version</MetricLabel>
                <MetricValue>{nodeInfo?.version || 'v1.10.8'}</MetricValue>
              </MetricItem>
              <MetricItem>
                <MetricLabel>Sync Status</MetricLabel>
                <MetricValue style={{ color: syncStatus?.syncing ? '#fbbf24' : '#4ade80' }}>
                  {syncStatus?.percentage || '100'}%
                </MetricValue>
              </MetricItem>
              <MetricItem>
                <MetricLabel>Chain ID</MetricLabel>
                <MetricValue>{nodeInfo?.chainId || '1'}</MetricValue>
              </MetricItem>
              <MetricItem>
                <MetricLabel>Network ID</MetricLabel>
                <MetricValue>{nodeInfo?.networkId || '1'}</MetricValue>
              </MetricItem>
            </MetricsGrid>
          </Card>

          {selectedBlock && (
            <Card>
              <CardTitle>🔍 Block Details</CardTitle>
              <MetricsGrid>
                <MetricItem>
                  <MetricLabel>Block Number</MetricLabel>
                  <MetricValue>{formatNumber(selectedBlock.number)}</MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>Transactions</MetricLabel>
                  <MetricValue>{selectedBlock.transactions}</MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>Gas Used</MetricLabel>
                  <MetricValue>{formatNumber(selectedBlock.gasUsed)}</MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>Size</MetricLabel>
                  <MetricValue>{formatNumber(selectedBlock.size)} bytes</MetricValue>
                </MetricItem>
              </MetricsGrid>
              <div style={{ marginTop: '15px', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                <div><strong>Hash:</strong> {selectedBlock.hash}</div>
                <div style={{ marginTop: '5px' }}><strong>Miner:</strong> {selectedBlock.miner}</div>
                <div style={{ marginTop: '5px' }}><strong>Timestamp:</strong> {new Date(selectedBlock.timestamp).toLocaleString()}</div>
              </div>
            </Card>
          )}
        </Sidebar>
      </ContentGrid>
    </Container>
  );
};

export default BlockchainExplorer; 